import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Box,
  Breadcrumb,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  IconButton,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Link, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { Controller, useFieldArray, useForm } from "react-hook-form"
import { FaMinus, FaPlus } from "react-icons/fa"

import { Field } from "@/components/ui/field"
import { RichTextEditor } from "@/components/ui/rich-text-editor"

import { useCreateTopics } from "@/hooks/useCreateTopics"
import { containsLocalImageUrls } from "@/utils/richTextImages"
import { GenerateRationaleButton } from "../GenerateRationaleButton"
import { SubjectSelector } from "../SubjectSelector"
import { SuggestTopicsButton } from "../SuggestTopicsButton"
import { TopicSelector } from "../TopicSelector"
import { TopicSuggestionsReview } from "../TopicSuggestionsReview"

import {
  type QuestionFormData,
  QuestionFormProvider,
  useQuestionForm,
} from "./QuestionFormContext"

interface QuestionFormProps {
  mode: "add" | "edit"
  questionId?: string
}

function QuestionFormContent() {
  const navigate = useNavigate()
  const {
    mode,
    questionId,
    selectedSubjects,
    selectedTopics,
    loadingQuestion,
    isSubmitting,
    handleSubjectsChange,
    handleTopicsChange,
    onSubmit,
    showRescoreConfirmation,
    rescoreImpact,
    confirmRescoreAndSubmit,
    cancelRescoreConfirmation,
    initializeForm,
  } = useQuestionForm()

  const createTopicsMutation = useCreateTopics()

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<QuestionFormData>({
    defaultValues: {
      question_text: "",
      rationale: "",
      options: [{ value: "" }, { value: "" }, { value: "" }, { value: "" }],
      correct_answer: 0,
      subject_ids: [],
      topic_ids: [],
      allow_practice_quiz: false,
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  })
  const [richTextUploadCount, setRichTextUploadCount] = useState(0)
  const [richTextImageError, setRichTextImageError] = useState("")
  const isUploadingRichTextImage = richTextUploadCount > 0
  const handleRichTextUploadStateChange = (isUploading: boolean) => {
    setRichTextUploadCount((count) =>
      Math.max(0, count + (isUploading ? 1 : -1)),
    )
  }

  const subjectIds = watch("subject_ids")

  // Initialize form with context's setValue function
  useEffect(() => {
    initializeForm(setValue)
  }, [initializeForm, setValue])

  if (mode === "edit" && loadingQuestion) {
    return (
      <Container maxW="container.lg" py={8}>
        <Text>Loading question...</Text>
      </Container>
    )
  }

  const canSubmit =
    subjectIds && subjectIds.length > 0 && !isUploadingRichTextImage

  const handleFormSubmit = (data: QuestionFormData) => {
    if (
      isUploadingRichTextImage ||
      containsLocalImageUrls(data.question_text) ||
      containsLocalImageUrls(data.rationale)
    ) {
      setRichTextImageError(
        "Wait for images to finish uploading before saving.",
      )
      return
    }

    setRichTextImageError("")
    return onSubmit(data)
  }

  const handleAddOption = () => {
    append({ value: "" })
  }

  const handleRemoveOption = (index: number) => {
    if (fields.length > 2) {
      remove(index)
      // Adjust correct_answer if it's pointing to a removed option
      const currentCorrect = watch("correct_answer")
      if (currentCorrect >= fields.length - 1) {
        setValue("correct_answer", fields.length - 2)
      }
    }
  }

  return (
    <Container maxW="container.xl" py={8}>
      <DialogRoot
        open={showRescoreConfirmation}
        onOpenChange={({ open }) => {
          if (!open) cancelRescoreConfirmation()
        }}
        role="alertdialog"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Answer Key Change</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack align="start" gap={3}>
              <Text>This question has already been answered.</Text>
              <Text>
                Changing the correct answer will automatically rescore affected
                attempts in the background.
              </Text>
              <Text>
                Student scores, percentiles, and analytics may change.
              </Text>
              {rescoreImpact && (
                <Text fontSize="sm" color="gray.600">
                  Impact: {rescoreImpact.affected_answers_count} answer(s),{" "}
                  {rescoreImpact.affected_attempts_count} attempt(s),{" "}
                  {rescoreImpact.affected_quizzes_count} quiz(zes).
                </Text>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={cancelRescoreConfirmation}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button colorPalette="red" onClick={confirmRescoreAndSubmit}>
              Confirm and Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <VStack align="stretch" gap={6}>
          {/* Breadcrumbs */}
          <Breadcrumb.Root>
            <Breadcrumb.List>
              <Breadcrumb.Item>
                <Breadcrumb.Link asChild>
                  <Link to="/questions">Questions</Link>
                </Breadcrumb.Link>
              </Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Item>
                <Breadcrumb.CurrentLink>
                  {mode === "add" ? "Add Question" : "Edit Question"}
                </Breadcrumb.CurrentLink>
              </Breadcrumb.Item>
            </Breadcrumb.List>
          </Breadcrumb.Root>

          {/* Header */}
          <Flex justify="space-between" align="center">
            <Heading size="xl">
              {mode === "add" ? "Add Question" : "Edit Question"}
            </Heading>
            <HStack>
              {mode === "edit" && questionId && (
                <>
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate({
                        to: "/questions/$questionId/history",
                        params: { questionId },
                      })
                    }
                  >
                    View History
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate({
                        to: "/questions/$questionId/analytics",
                        params: { questionId },
                      })
                    }
                  >
                    View Analytics
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={() => navigate({ to: "/questions" })}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorPalette="blue"
                disabled={!canSubmit}
                loading={isSubmitting || isUploadingRichTextImage}
              >
                {mode === "add" ? "Create Question" : "Update Question"}
              </Button>
            </HStack>
          </Flex>
          {richTextImageError && (
            <Text color="red.500" fontSize="sm">
              {richTextImageError}
            </Text>
          )}

          {/* Two-column layout */}
          <Grid
            templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
            gap={6}
            alignItems="start"
          >
            {/* Left Column */}
            <GridItem>
              <VStack align="stretch" gap={6}>
                {/* Question Text */}
                <Field
                  required
                  invalid={!!errors.question_text}
                  errorText={errors.question_text?.message}
                  label="Question Text"
                  helperText="Use the rich text editor to format your question"
                >
                  <Controller
                    name="question_text"
                    control={control}
                    rules={{ required: "Question text is required" }}
                    render={({ field }) => (
                      <RichTextEditor
                        value={field.value}
                        onChange={field.onChange}
                        onUploadStateChange={handleRichTextUploadStateChange}
                        onUploadError={setRichTextImageError}
                        placeholder="Enter the question text..."
                        minHeight="150px"
                      />
                    )}
                  />
                </Field>

                {/* Answer Options */}
                <Box>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Text fontWeight="semibold">
                      Answer Options{" "}
                      <Text as="span" color="red.500">
                        *
                      </Text>
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleAddOption}
                    >
                      <FaPlus />
                      Add Option
                    </Button>
                  </Flex>

                  <VStack align="stretch" gap={3}>
                    {fields.map((field, index) => (
                      <Flex key={field.id} gap={2} align="flex-start">
                        <Flex mt={-1} direction="column" pt={8} align="center">
                          <Checkbox
                            mt={2}
                            checked={watch("correct_answer") === index}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setValue("correct_answer", index)
                              }
                            }}
                            aria-label={`Mark option ${
                              index + 1
                            } as correct answer`}
                          />
                        </Flex>

                        <Field
                          flex={1}
                          required
                          invalid={!!errors.options?.[index]?.value}
                          errorText={errors.options?.[index]?.value?.message}
                          label={`Option ${index + 1}`}
                        >
                          <Input
                            {...register(`options.${index}.value`, {
                              required: "Option is required",
                            })}
                            placeholder={`Enter option ${index + 1}`}
                          />
                        </Field>

                        {fields.length > 2 && (
                          <Flex mt={-1} pt={8}>
                            <IconButton
                              size="sm"
                              variant="outline"
                              colorPalette="red"
                              onClick={() => handleRemoveOption(index)}
                              type="button"
                              aria-label="Remove option"
                            >
                              <FaMinus />
                            </IconButton>
                          </Flex>
                        )}
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              </VStack>
            </GridItem>

            {/* Right Column */}
            <GridItem>
              <VStack align="stretch" gap={6}>
                {/* Rationale */}
                <Field
                  width="100%"
                  label="Rationale (Explanation)"
                  helperText="Provide an explanation for the correct answer"
                >
                  <Flex gap={2} align="flex-start" width="100%">
                    <Box flex={1}>
                      <Controller
                        name="rationale"
                        control={control}
                        render={({ field }) => (
                          <RichTextEditor
                            value={field.value}
                            onChange={field.onChange}
                            onUploadStateChange={
                              handleRichTextUploadStateChange
                            }
                            onUploadError={setRichTextImageError}
                            placeholder="Explain why this is the correct answer..."
                            minHeight="150px"
                          />
                        )}
                      />
                    </Box>
                    <Box flexShrink={0}>
                      <GenerateRationaleButton
                        questionText={watch("question_text") || ""}
                        options={
                          watch("options")?.map((opt) => opt.value) || []
                        }
                        correctAnswer={watch("correct_answer")}
                        questionId={mode === "edit" ? questionId : undefined}
                        subjectIds={watch("subject_ids") || []}
                        topicIds={watch("topic_ids") || []}
                        onRationaleGenerated={(html) =>
                          setValue("rationale", html)
                        }
                        variant="icon"
                        disabled={
                          !watch("question_text") || watch("correct_answer") < 0
                        }
                      />
                    </Box>
                  </Flex>
                </Field>

                {/* Subjects */}
                <Field
                  required
                  invalid={!!errors.subject_ids}
                  errorText={errors.subject_ids?.message}
                  label="Subjects"
                  helperText="Search and select subjects for this question"
                >
                  <Controller
                    name="subject_ids"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value && value.length > 0
                          ? true
                          : "At least one subject is required",
                    }}
                    render={() => (
                      <SubjectSelector
                        selectedSubjects={selectedSubjects}
                        onChange={handleSubjectsChange}
                      />
                    )}
                  />
                </Field>

                {/* Topics */}
                <Field
                  width="100%"
                  label="Topics"
                  helperText="Search and select topics, or create new ones"
                >
                  <Flex gap={2} align="flex-start" width="100%">
                    <Box flex={1}>
                      <TopicSelector
                        selectedTopics={selectedTopics}
                        onChange={handleTopicsChange}
                      />
                    </Box>
                    <Box flexShrink={0}>
                      <SuggestTopicsButton
                        questionText={watch("question_text") || ""}
                        options={
                          watch("options")?.map((opt) => opt.value) || []
                        }
                        questionId={mode === "edit" ? questionId : undefined}
                        subjectIds={watch("subject_ids") || []}
                        onTopicsSuggested={async (topicNames) => {
                          // Create topics via API first to get real IDs
                          const result =
                            await createTopicsMutation.mutateAsync(topicNames)

                          // Add successfully created topics with real IDs from API
                          const newTopics = result.createdTopics.map(
                            (topic) => ({
                              id: topic.id,
                              name: topic.name,
                            }),
                          )
                          const combined = [...selectedTopics, ...newTopics]
                          handleTopicsChange(combined)
                        }}
                        variant="icon"
                        disabled={
                          !watch("question_text") ||
                          !watch("options")?.every((opt) => opt.value.trim())
                        }
                      />
                    </Box>
                  </Flex>
                </Field>

                {mode === "edit" && questionId && (
                  <TopicSuggestionsReview questionId={questionId} />
                )}

                {/* Allow Practice Quiz */}
                <Field
                  label="Practice Quiz Eligibility"
                  helperText="When enabled, this question can be included in student-generated practice quizzes. Questions used in graded assessments are typically disabled."
                >
                  <Controller
                    name="allow_practice_quiz"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(e) => field.onChange(e.checked)}
                      >
                        Allow in practice quizzes
                      </Checkbox>
                    )}
                  />
                </Field>
              </VStack>
            </GridItem>
          </Grid>
        </VStack>
      </form>
    </Container>
  )
}

function QuestionForm({ mode, questionId }: QuestionFormProps) {
  return (
    <QuestionFormProvider mode={mode} questionId={questionId}>
      <QuestionFormContent />
    </QuestionFormProvider>
  )
}

export default QuestionForm
